import { gql } from '@/__generated__';
import { useQuery } from '@apollo/client';
import { SortOrder } from '@/__generated__/graphql';
import {
  Badge,
  Box,
  Group,
  Loader,
  Paper,
  Progress,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Text,
  Title,
} from '@mantine/core';
import { BarChart, CompositeChart, ChartReferenceLineProps } from '@mantine/charts';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { useState } from 'react';
import { livesIdDocument } from '../live/graphql/queries';

dayjs.extend(customParseFormat);

const liveStatisticsDocument = gql(/* GraphQL */ `
  query LiveStatistics($liveId: String) {
    liveStatistics(liveId: $liveId) {
      cancelledTotal
      cancelledCount
      realLostTotal
      realLostCustomerCount
      swapTotal
      swapCustomerCount
      adminCancelledTotal
      adminCancelledCustomerCount
      intervals {
        interval
        total
        count
        cancelledTotal
        cancelledCount
      }
      products {
        productId
        productName
        total
        cancelledTotal
        cancelledCount
      }
    }
  }
`);

// Absolute thresholds tuned against real live-sale data (Apr-May 2026 lives).
// Live broadcast activity = 5+ product orders per 5-min window. VOD/trickle is below this.
const ACTIVE_COUNT_FLOOR = 5;
// At 5-min buckets, 25 min = 5 quiet buckets in a row — enough for a short pause,
// not enough to merge two separate stream sessions.
const STREAM_GAP_MINUTES = 25;
// A stream session is at least 15 min of active selling (3 active buckets) — under that
// is a noise spike.
const MIN_ACTIVE_BUCKETS_PER_STREAM = 3;

type IntervalRow = {
  interval: string;
  total: number;
  count: number;
  cancelledTotal: number;
  cancelledCount: number;
};

type StreamRow = {
  index: number;
  start: dayjs.Dayjs;
  end: dayjs.Dayjs;
  total: number;
  count: number;
  cancelledTotal: number;
  cancelledCount: number;
  bucketTotals: number[];
  cancelledBucketTotals: number[];
  peak: { total: number; time: dayjs.Dayjs } | null;
  vod: {
    end: dayjs.Dayjs;
    total: number;
    count: number;
    cancelledTotal: number;
    cancelledCount: number;
  } | null;
};

const STREAM_COLORS = [
  'green',
  'blue',
  'orange',
  'pink',
  'violet',
  'cyan',
  'teal',
  'red',
  'yellow',
  'indigo',
];
const streamColorName = (idx: number) => STREAM_COLORS[(idx - 1) % STREAM_COLORS.length];
const streamColorShade = (idx: number, shade = 6) => `${streamColorName(idx)}.${shade}`;

const calcActiveThreshold = (_intervals: IntervalRow[]): number => {
  // Absolute floor only. Percentile-based threshold over-fit to live intensity and
  // missed obvious quiet streams while including loud trickle bursts.
  return ACTIVE_COUNT_FLOOR;
};

const buildStreams = (intervals: IntervalRow[]): StreamRow[] => {
  const threshold = calcActiveThreshold(intervals);
  const withTime = intervals.map((i) => ({ ...i, t: dayjs(i.interval, 'DD.MM. HH:mm') }));

  // Cluster active buckets into streams by time-gap (active buckets only — VOD trickle ignored here).
  type Cluster = { firstT: dayjs.Dayjs; lastActiveT: dayjs.Dayjs; activeBucketCount: number };
  const rawClusters: Cluster[] = [];
  for (const row of withTime) {
    if (row.count < threshold) continue;
    const last = rawClusters[rawClusters.length - 1];
    if (!last || row.t.diff(last.lastActiveT, 'minute') > STREAM_GAP_MINUTES) {
      rawClusters.push({ firstT: row.t, lastActiveT: row.t, activeBucketCount: 1 });
    } else {
      last.lastActiveT = row.t;
      last.activeBucketCount += 1;
    }
  }

  // Drop noise: clusters with too few active buckets are single spikes, not streams.
  const clusters = rawClusters.filter((c) => c.activeBucketCount >= MIN_ACTIVE_BUCKETS_PER_STREAM);

  if (clusters.length === 0) return [];

  const dataLastT = withTime[withTime.length - 1]?.t;
  const dataEndExclusive = dataLastT ? dataLastT.add(15, 'minute') : null;

  return clusters.map((c, idx) => {
    const streamEndExclusive = c.lastActiveT.add(15, 'minute');
    const inStream = withTime.filter(
      (r) => !r.t.isBefore(c.firstT) && r.t.isBefore(streamEndExclusive)
    );
    const streamTotal = inStream.reduce((s, r) => s + r.total, 0);
    const streamCount = inStream.reduce((s, r) => s + r.count, 0);
    const streamCancelledTotal = inStream.reduce((s, r) => s + r.cancelledTotal, 0);
    const streamCancelledCount = inStream.reduce((s, r) => s + r.cancelledCount, 0);
    const bucketTotals = inStream.map((r) => r.total);
    const cancelledBucketTotals = inStream.map((r) => r.cancelledTotal);

    let peak: StreamRow['peak'] = null;
    if (inStream.length > 0) {
      const peakRow = inStream.reduce((best, r) => (r.total > best.total ? r : best), inStream[0]);
      peak = { total: peakRow.total, time: peakRow.t };
    }

    const nextStart = clusters[idx + 1]?.firstT;
    const vodEndExclusive = nextStart ?? dataEndExclusive;
    let vod: StreamRow['vod'] = null;
    if (vodEndExclusive && streamEndExclusive.isBefore(vodEndExclusive)) {
      const inVod = withTime.filter(
        (r) => !r.t.isBefore(streamEndExclusive) && r.t.isBefore(vodEndExclusive)
      );
      const vodTotal = inVod.reduce((s, r) => s + r.total, 0);
      const vodCount = inVod.reduce((s, r) => s + r.count, 0);
      const vodCancelledTotal = inVod.reduce((s, r) => s + r.cancelledTotal, 0);
      const vodCancelledCount = inVod.reduce((s, r) => s + r.cancelledCount, 0);
      if (vodCount > 0 || vodCancelledCount > 0) {
        vod = {
          end: vodEndExclusive.subtract(15, 'minute'),
          total: vodTotal,
          count: vodCount,
          cancelledTotal: vodCancelledTotal,
          cancelledCount: vodCancelledCount,
        };
      }
    }

    return {
      index: idx + 1,
      start: c.firstT,
      end: c.lastActiveT,
      total: streamTotal,
      count: streamCount,
      cancelledTotal: streamCancelledTotal,
      cancelledCount: streamCancelledCount,
      bucketTotals,
      cancelledBucketTotals,
      peak,
      vod,
    };
  });
};

const formatDuration = (minutes: number) => {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('sk-SK', { style: 'currency', currency: 'EUR' }).format(value);

export const LiveStatisticsPage = () => {
  const [selectedLiveId, setSelectedLiveId] = useState<string | null>(null);

  const { data: livesData } = useQuery(livesIdDocument, {
    variables: { orderBy: [{ date: SortOrder.Desc }] },
  });

  const { data, loading } = useQuery(liveStatisticsDocument, {
    variables: { liveId: selectedLiveId },
  });
  if (loading || !data?.liveStatistics) return <Loader />;

  const {
    intervals,
    products,
    cancelledCount: liveCancelledCount,
    realLostTotal,
    realLostCustomerCount,
    swapTotal,
    swapCustomerCount,
    adminCancelledTotal,
    adminCancelledCustomerCount,
  } = data.liveStatistics;
  if (intervals.length === 0) return <Text>Žiadne predaje v aktívnom live.</Text>;

  // Sort intervals chronologically (server returns insertion order from Map)
  const sortedIntervals = [...intervals].sort(
    (a, b) =>
      dayjs(a.interval, 'DD.MM. HH:mm').toDate().getTime() -
      dayjs(b.interval, 'DD.MM. HH:mm').toDate().getTime()
  );

  const streams = buildStreams(sortedIntervals);

  // Identify the "best" stream by stream+VOD total (for the 🏆 chip).
  const bestStreamIdx = streams
    .map((s, i) => ({ i, total: s.total + (s.vod?.total ?? 0) }))
    .sort((a, b) => b.total - a.total)[0]?.i;

  // Aggregate 5-min buckets → 30-min for the main composite chart. Hoisted here so
  // reference-line X values can be snapped to actual chart tick labels (otherwise
  // recharts silently drops lines whose x doesn't match an axis value).
  const BUCKETS_PER_GROUP = 6;
  type Agg = { interval: string; total: number; cancelledTotal: number };
  const grouped: Agg[] = [];
  for (let i = 0; i < sortedIntervals.length; i += BUCKETS_PER_GROUP) {
    const group = sortedIntervals.slice(i, i + BUCKETS_PER_GROUP);
    if (group.length === 0) continue;
    grouped.push({
      interval: group[0].interval,
      total: group.reduce((s, b) => s + b.total, 0),
      cancelledTotal: group.reduce((s, b) => s + b.cancelledTotal, 0),
    });
  }

  // Stream-boundary vertical lines — snap each stream start to the matching 30-min bucket.
  const referenceLines: ChartReferenceLineProps[] = [];
  for (const s of streams) {
    const match = grouped.find((g) => {
      const gt = dayjs(g.interval, 'DD.MM. HH:mm');
      return gt.isSame(s.start) || gt.isAfter(s.start);
    });
    if (!match) continue;
    referenceLines.push({
      x: match.interval,
      label: `#${s.index}`,
      color: streamColorShade(s.index),
      labelPosition: 'insideTopRight',
    });
  }

  // Top-N products, sorted by net (sold - cancelled), with % + cancellation suffix.
  const productsTotal = products.reduce((sum, p) => sum + p.total, 0);
  const sortedProducts = [...products].sort(
    (a, b) => b.total - b.cancelledTotal - (a.total - a.cancelledTotal)
  );
  const TOP_N = 15;
  const topProducts = sortedProducts.slice(0, TOP_N);
  const labelForProduct = (name: string, total: number) => {
    const pct = productsTotal > 0 ? Math.round((total / productsTotal) * 100) : 0;
    return `${name} (${pct} %)`;
  };
  const productData = topProducts.map((p) => ({
    productName: labelForProduct(p.productName, p.total),
    'Tržby (€)': p.total,
  }));

  const liveStart = streams[0]?.start;
  const lastStream = streams[streams.length - 1];
  const liveEnd = lastStream
    ? (lastStream.vod?.end ?? lastStream.end).add(15, 'minute')
    : undefined;
  const liveCount = streams.reduce((sum, s) => sum + s.count + (s.vod?.count ?? 0), 0);
  const liveTotal = streams.reduce((sum, s) => sum + s.total + (s.vod?.total ?? 0), 0);

  const liveOptions = (livesData?.lives ?? []).map((l) => ({
    value: l.id,
    label: l.name,
  }));

  return (
    <Stack gap="xl">
      <Select
        label="Live"
        placeholder="Aktívny live"
        data={liveOptions}
        value={selectedLiveId}
        onChange={setSelectedLiveId}
        searchable
        clearable
        maw={400}
      />
      <Stack gap="xs">
        <Title order={3}>🎥 Streamy</Title>
        <Paper withBorder p="md">
          {liveStart && liveEnd && (
            <Stack gap={2} mb="sm">
              <Text fw={600}>
                Live {liveStart.format('DD.MM. HH:mm')} — {liveEnd.format('DD.MM. HH:mm')} ·{' '}
                {liveCount} ks · {formatCurrency(liveTotal)}
              </Text>
              {(liveCancelledCount > 0 || adminCancelledTotal > 0) && (
                <Text size="sm" c="dimmed">
                  {liveCancelledCount > 0 ? (
                    <>
                      ↳ mohli sme mať {formatCurrency(liveTotal + realLostTotal)} ·{' '}
                      <Text component="span" c="red.6" fw={600}>
                        stratené −{formatCurrency(realLostTotal)} ({realLostCustomerCount}{' '}
                        {realLostCustomerCount === 1 ? 'zákazník' : 'zákazníkov'})
                      </Text>
                    </>
                  ) : (
                    '↳ '
                  )}
                  {adminCancelledTotal > 0 && (
                    <Text component="span" c="orange.6" fw={600}>
                      {liveCancelledCount > 0 ? ' · ' : ''}zrušené −
                      {formatCurrency(adminCancelledTotal)} ({adminCancelledCustomerCount}{' '}
                      {adminCancelledCustomerCount === 1 ? 'zákazník' : 'zákazníkov'})
                    </Text>
                  )}
                  {swapTotal > 0 && (
                    <>
                      {' · '}swap {formatCurrency(swapTotal)} ({swapCustomerCount}{' '}
                      {swapCustomerCount === 1 ? 'zákazník' : 'zákazníkov'})
                    </>
                  )}
                </Text>
              )}
            </Stack>
          )}
          <Table striped verticalSpacing="sm">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Stream</Table.Th>
              <Table.Th>Časový rozsah</Table.Th>
              <Table.Th>Trvanie</Table.Th>
              <Table.Th>Predaje</Table.Th>
              <Table.Th>Tržby · podiel</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {streams.map((s, i) => {
              const streamEnd = s.end.add(15, 'minute');
              const streamMinutes = streamEnd.diff(s.start, 'minute');
              const streamHours = streamMinutes / 60;
              const vodMinutes = s.vod
                ? s.vod.end.add(15, 'minute').diff(streamEnd, 'minute')
                : 0;
              const streamPercent = liveTotal > 0 ? (s.total / liveTotal) * 100 : 0;
              const vodPercent =
                liveTotal > 0 && s.vod ? (s.vod.total / liveTotal) * 100 : 0;
              const sharePercent = streamPercent + vodPercent;
              const avgPerSale = s.count > 0 ? s.total / s.count : 0;
              const perHour = streamHours > 0 ? s.total / streamHours : 0;
              const vodEfficiency = s.vod && s.total > 0 ? (s.vod.total / s.total) * 100 : null;
              const isBest = i === bestStreamIdx;
              return (
                <Table.Tr key={s.index}>
                  <Table.Td style={{ verticalAlign: 'top' }}>
                    <Group gap={6} wrap="nowrap">
                      <Box
                        w={10}
                        h={10}
                        bg={streamColorShade(s.index)}
                        style={{ borderRadius: '50%' }}
                      />
                      <Text fw={600}>#{s.index}</Text>
                      {isBest && (
                        <Badge size="xs" color="yellow" variant="filled">
                          🏆
                        </Badge>
                      )}
                    </Group>
                  </Table.Td>
                  <Table.Td style={{ verticalAlign: 'top' }}>
                    <Stack gap={2}>
                      <Text size="sm">
                        {s.start.format('DD.MM. HH:mm')} → {streamEnd.format('DD.MM. HH:mm')}
                      </Text>
                      {s.vod && (
                        <Text size="xs" c="dimmed">
                          + VOD do {s.vod.end.add(15, 'minute').format('DD.MM. HH:mm')}
                        </Text>
                      )}
                    </Stack>
                  </Table.Td>
                  <Table.Td style={{ verticalAlign: 'top' }}>
                    <Stack gap={2}>
                      <Text size="sm">{formatDuration(streamMinutes)}</Text>
                      <Text size="xs" c="dimmed">
                        {formatCurrency(perHour)}/h
                      </Text>
                      {vodMinutes > 0 && (
                        <Text size="xs" c="dimmed">
                          + {formatDuration(vodMinutes)} VOD
                        </Text>
                      )}
                    </Stack>
                  </Table.Td>
                  <Table.Td style={{ verticalAlign: 'top' }}>
                    <Stack gap={2}>
                      <Text size="sm">{s.count} ks</Text>
                      <Text size="xs" c="dimmed">
                        {formatCurrency(avgPerSale)}/ks
                      </Text>
                      {s.vod && s.vod.count > 0 && (
                        <Text size="xs" c="dimmed">
                          + {s.vod.count} VOD
                        </Text>
                      )}
                    </Stack>
                  </Table.Td>
                  <Table.Td style={{ verticalAlign: 'top', minWidth: 200 }}>
                    <Stack gap={4}>
                      <Text size="sm" fw={600}>
                        {formatCurrency(s.total)}
                      </Text>
                      {s.vod && s.vod.total > 0 && (
                        <Text size="xs" c="dimmed">
                          + {formatCurrency(s.vod.total)} VOD
                          {vodEfficiency !== null && ` (${vodEfficiency.toFixed(0)} %)`}
                        </Text>
                      )}
                      <Progress.Root size="sm" mt={4}>
                        <Progress.Section
                          value={streamPercent}
                          color={streamColorShade(s.index)}
                        />
                        {vodPercent > 0 && (
                          <Progress.Section
                            value={vodPercent}
                            color={streamColorShade(s.index, 3)}
                          />
                        )}
                      </Progress.Root>
                      <Text size="xs" c="dimmed">
                        {sharePercent.toFixed(1)} % z live
                      </Text>
                    </Stack>
                  </Table.Td>
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
        </Paper>
      </Stack>

      {streams.length > 0 && (
        <Stack gap="xs">
          <Title order={3}>📊 Aktivita po streamoch</Title>
          <SimpleGrid cols={1}>
            {streams.map((s) => {
              const streamEnd = s.end.add(15, 'minute');
              const buckets = sortedIntervals.filter((b) => {
                const t = dayjs(b.interval, 'DD.MM. HH:mm');
                return !t.isBefore(s.start) && t.isBefore(streamEnd);
              });
              return (
                <Paper key={s.index} withBorder p="sm">
                  <Box
                    bg={streamColorShade(s.index)}
                    h={3}
                    mb="xs"
                    style={{ borderRadius: 2 }}
                  />
                  <Stack gap={4}>
                    <Text fw={600} size="sm">
                      Stream #{s.index} · {s.start.format('DD.MM. HH:mm')} →{' '}
                      {streamEnd.format('HH:mm')}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {s.count} ks · {formatCurrency(s.total)}
                    </Text>
                    {(() => {
                      let streamRunning = 0;
                      const compositeData = buckets.map((b) => {
                        streamRunning += b.total - b.cancelledTotal;
                        return {
                          time: b.interval.split(' ')[1],
                          'Tržby (€)': b.total,
                          'Čisté (€)': streamRunning,
                        };
                      });
                      const cancelData = buckets.map((b) => ({
                        time: b.interval.split(' ')[1],
                        'Zrušené (€)': b.cancelledTotal,
                      }));
                      const anyCancel = buckets.some((b) => b.cancelledTotal > 0);
                      return (
                        <Stack gap={0}>
                          <CompositeChart
                            h={280}
                            data={compositeData}
                            dataKey="time"
                            series={[
                              {
                                name: 'Tržby (€)',
                                type: 'bar',
                                color: streamColorShade(s.index),
                              },
                              { name: 'Čisté (€)', type: 'line', color: 'gray.0' },
                            ]}
                            withDots={false}
                            withLegend={false}
                            strokeWidth={2}
                            withXAxis={!anyCancel}
                          />
                          {anyCancel && (
                            <BarChart
                              h={70}
                              data={cancelData}
                              dataKey="time"
                              series={[{ name: 'Zrušené (€)', color: 'red.6' }]}
                              withLegend={false}
                            />
                          )}
                        </Stack>
                      );
                    })()}
                  </Stack>
                </Paper>
              );
            })}
          </SimpleGrid>
        </Stack>
      )}

      <Stack gap="xs">
        <Title order={3}>📈 Čistý priebeh tržieb</Title>
        {(() => {
          let mainRunning = 0;
          const composedData = grouped.map((p) => {
            mainRunning += p.total - p.cancelledTotal;
            const t = dayjs(p.interval, 'DD.MM. HH:mm');
            // Use the 30-min window when attributing to a stream
            const stream = streams.find(
              (s) => !t.isBefore(s.start) && t.isBefore(s.end.add(30, 'minute'))
            );
            const row: Record<string, string | number> = { interval: p.interval };
            for (const s of streams) row[`#${s.index}`] = 0;
            row['VOD'] = 0;
            row['Čisté kumulatívne'] = mainRunning;
            if (stream) row[`#${stream.index}`] = p.total;
            else row['VOD'] = p.total;
            return row;
          });
          const composedSeries = [
            ...streams.map((s) => ({
              name: `#${s.index}`,
              type: 'bar' as const,
              color: streamColorShade(s.index),
            })),
            { name: 'VOD', type: 'bar' as const, color: 'gray.5' },
            { name: 'Čisté kumulatívne', type: 'line' as const, color: 'gray.0' },
          ];
          // Cancellation strip data (positive values — bars go up; header makes meaning obvious).
          const cancelStripData = grouped.map((g) => ({
            interval: g.interval,
            'Zrušené (€)': g.cancelledTotal,
          }));
          const anyCancel = grouped.some((g) => g.cancelledTotal > 0);
          return (
            <Stack gap={0}>
              <CompositeChart
                h={anyCancel ? 340 : 400}
                data={composedData}
                dataKey="interval"
                series={composedSeries}
                withDots={false}
                withLegend
                strokeWidth={2}
                yAxisLabel="Tržby (€)"
                referenceLines={referenceLines}
                withXAxis={!anyCancel}
              />
              {anyCancel && (
                <BarChart
                  h={90}
                  data={cancelStripData}
                  dataKey="interval"
                  series={[{ name: 'Zrušené (€)', color: 'red.6' }]}
                  withLegend={false}
                  xAxisLabel="Čas (30 min)"
                />
              )}
            </Stack>
          );
        })()}
      </Stack>

      <Stack gap="xs">
        <Title order={3}>🛍 Predaj podľa produktu (top {TOP_N})</Title>
        <BarChart
          h={500}
          data={productData}
          dataKey="productName"
          series={[{ name: 'Tržby (€)', color: 'purple' }]}
          orientation="horizontal"
          xAxisLabel="Tržby (€)"
          yAxisLabel="Produkt"
        />
      </Stack>
    </Stack>
  );
};
